import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@/config/firebase';

// Storage paths
const STORAGE_ROOT = 'Product Manager Uploaded Images';

export interface ImageUploadResult {
  url: string;
  path: string;
  fileName: string;
}

export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeBytes?: number;
}

/**
 * Compress an image file using canvas
 */
export const compressImage = async (
  file: File,
  options: ImageCompressionOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1600,
    maxHeight = 900,
    quality = 0.85,
    maxSizeBytes = 5 * 1024 * 1024 // 5MB
  } = options;

  // Validate input
  if (!file) {
    throw new Error('No file provided for compression');
  }

  // If file is already small enough, return as-is
  if (file.size <= maxSizeBytes && file.size <= 1024 * 1024) { // 1MB threshold for compression
    return file;
  }

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    const img = new Image();
    let objectUrl: string | null = null;

    // Cleanup function
    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

    // Set up timeout for image loading
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Image loading timeout'));
    }, 30000); // 30 second timeout

    img.onload = () => {
      try {
        clearTimeout(timeout);
        
        // Calculate new dimensions
        let { width, height } = img;
        
        // Validate image dimensions
        if (width <= 0 || height <= 0) {
          cleanup();
          reject(new Error('Invalid image dimensions'));
          return;
        }
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            cleanup();
            
            if (blob) {
              // Validate compressed file size
              if (blob.size > maxSizeBytes) {
                reject(new Error('Compressed image still too large'));
                return;
              }
              
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image - blob creation failed'));
            }
          },
          file.type,
          Math.max(0.1, Math.min(1.0, quality)) // Ensure quality is between 0.1 and 1.0
        );
      } catch (error) {
        cleanup();
        reject(new Error(`Image compression failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    img.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      reject(new Error('Failed to load image - file may be corrupted'));
    };

    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      clearTimeout(timeout);
      reject(new Error('Failed to create object URL from file'));
    }
  });
};

/**
 * Validate image file
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  const minSize = 1024; // 1KB minimum

  // Check if file exists
  if (!file) {
    return {
      valid: false,
      error: 'No file provided.'
    };
  }

  // Check file name
  if (!file.name || file.name.trim() === '') {
    return {
      valid: false,
      error: 'Invalid file name.'
    };
  }

  // Check file type
  if (!file.type || !allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload JPG, PNG, GIF, or WebP images.'
    };
  }

  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size too large. Maximum size is 5MB.'
    };
  }

  if (file.size < minSize) {
    return {
      valid: false,
      error: 'File size too small. Minimum size is 1KB.'
    };
  }

  // Check for suspicious file extensions in name
  const suspiciousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.pif', '.com', '.js', '.vbs'];
  const fileName = file.name.toLowerCase();
  if (suspiciousExtensions.some(ext => fileName.includes(ext))) {
    return {
      valid: false,
      error: 'File contains suspicious content.'
    };
  }

  return { valid: true };
};

/**
 * Generate unique filename with timestamp
 */
const generateFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 15);
  const extension = originalName.split('.').pop();
  return `${timestamp}_${randomId}.${extension}`;
};

/**
 * Upload image for task description
 */
export const uploadTaskImage = async (
  taskId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ImageUploadResult> => {
  // Validate inputs
  if (!taskId || taskId.trim() === '') {
    throw new Error('Task ID is required for image upload');
  }

  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    // Compress image
    const compressedFile = await compressImage(file);
    
    // Generate storage path
    const fileName = generateFileName(file.name);
    const storagePath = `${STORAGE_ROOT}/tasks/${taskId}/description/${fileName}`;
    const storageRef = ref(storage, storagePath);

    // Upload file
    const snapshot = await uploadBytes(storageRef, compressedFile);
    
    // Validate upload result
    if (!snapshot || !snapshot.ref) {
      throw new Error('Upload completed but no reference returned');
    }
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Validate download URL
    if (!downloadURL || !downloadURL.startsWith('https://')) {
      throw new Error('Invalid download URL received');
    }

    return {
      url: downloadURL,
      path: storagePath,
      fileName: fileName
    };
  } catch (error) {
    console.error('Error uploading task image:', error);
    
    // Provide specific error messages
    if (error instanceof Error) {
      if (error.message.includes('storage/unauthorized')) {
        throw new Error('You do not have permission to upload images');
      } else if (error.message.includes('storage/quota-exceeded')) {
        throw new Error('Storage quota exceeded. Please contact administrator');
      } else if (error.message.includes('storage/unauthenticated')) {
        throw new Error('Please log in to upload images');
      } else if (error.message.includes('network')) {
        throw new Error('Network error. Please check your connection and try again');
      } else if (error.message.includes('compress')) {
        throw new Error('Failed to process image. Please try a different image');
      } else {
        throw error; // Re-throw if it's already a user-friendly error
      }
    }
    
    throw new Error('Failed to upload image. Please try again');
  }
};

/**
 * Upload image for comment
 */
export const uploadCommentImage = async (
  commentId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ImageUploadResult> => {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Compress image
  const compressedFile = await compressImage(file);
  
  // Generate storage path
  const fileName = generateFileName(file.name);
  const storagePath = `${STORAGE_ROOT}/comments/${commentId}/${fileName}`;
  const storageRef = ref(storage, storagePath);

  try {
    // Upload file
    const snapshot = await uploadBytes(storageRef, compressedFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: storagePath,
      fileName: fileName
    };
  } catch (error) {
    console.error('Error uploading comment image:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
};

/**
 * Delete image from storage
 */
export const deleteImage = async (imageUrl: string): Promise<void> => {
  try {
    // Validate input
    if (!imageUrl || typeof imageUrl !== 'string') {
      throw new Error('Invalid image URL provided');
    }

    // Check if it's a Firebase Storage URL
    if (!imageUrl.includes('firebasestorage.googleapis.com')) {
      console.warn('URL is not a Firebase Storage URL, skipping deletion:', imageUrl);
      return; // Don't throw error, just skip deletion
    }

    // Extract storage path from URL
    const url = new URL(imageUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
    
    if (!pathMatch || !pathMatch[1]) {
      console.warn('Could not extract storage path from URL:', imageUrl);
      return; // Don't throw error, just skip deletion
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    
    // Validate storage path
    if (!storagePath || storagePath.trim() === '') {
      console.warn('Empty storage path extracted from URL:', imageUrl);
      return; // Don't throw error, just skip deletion
    }

    const storageRef = ref(storage, storagePath);
    
    await deleteObject(storageRef);
    console.log('Successfully deleted image from storage:', storagePath);
  } catch (error) {
    console.error('Error deleting image:', error);
    
    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('storage/object-not-found')) {
        console.warn('Image already deleted or does not exist:', imageUrl);
        return; // Don't throw error if image doesn't exist
      } else if (error.message.includes('storage/unauthorized')) {
        throw new Error('You do not have permission to delete this image');
      } else if (error.message.includes('storage/unauthenticated')) {
        throw new Error('Please log in to delete images');
      }
    }
    
    throw new Error('Failed to delete image');
  }
};

/**
 * Delete all images for a task (including comments)
 */
export const deleteTaskImages = async (taskId: string, imageUrls: string[]): Promise<void> => {
  const deletePromises = imageUrls.map(url => deleteImage(url));
  
  try {
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting task images:', error);
    // Don't throw error to prevent blocking task deletion
  }
};

/**
 * Delete all images for a comment
 */
export const deleteCommentImages = async (imageUrls: string[]): Promise<void> => {
  const deletePromises = imageUrls.map(url => deleteImage(url));
  
  try {
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting comment images:', error);
    // Don't throw error to prevent blocking comment deletion
  }
};

/**
 * Extract storage path from Firebase Storage URL
 */
export const getStoragePathFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+?)\?/);
    return pathMatch ? decodeURIComponent(pathMatch[1]) : null;
  } catch {
    return null;
  }
};